import { z } from 'zod';
import apiClient from '@shared/api/http';
import { CurrentUserDto, SessionDto } from '@shared/api/generated/contract';
import type { AuthUser, LoginResponse } from '../domain/session';
import type { OtpSession } from './otpSession';
import { expandLegacyPermissions } from './permissionCompat';

// v1 reorganizó auth bajo `/auth/password/*`, `/auth/session/*` y `/auth/otp/*`, y cambió la forma
// de la sesión: donde el legado devolvía `{ token, refreshToken, user }` planos, ahora llega
// `{ session: SessionDto, user: CurrentUserDto }` (mapping.md §S-6).
const SessionResponseSchema = z.object({
  session: SessionDto,
  user: CurrentUserDto,
});

/**
 * Adapta `CurrentUserDto` a la forma que ya consumen `domain/session.rules.ts` y la UI.
 *
 * v1 mueve los permisos al nivel raíz y expone `primaryRole`, mientras `hasPermission()` los busca
 * en `user.role.permissions`. El DTO se propaga entero además del envoltorio de compatibilidad
 * porque `AuthUser` es un contenedor abierto que se persiste tal cual en localStorage.
 */
function toAuthUser(dto: z.infer<typeof CurrentUserDto>): AuthUser {
  const fullName = [dto.firstName, dto.lastName].filter(Boolean).join(' ');
  const permissions = expandLegacyPermissions(dto.permissions);
  return {
    ...dto,
    name: fullName || dto.username,
    // v1 usa `null` para "sin idioma preferido"; el dominio y `applyLanguage()` esperan ausencia.
    preferredLanguage: dto.preferredLanguage ?? undefined,
    permissions,
    role: { name: dto.primaryRole?.name, permissions },
    roles: dto.roles.map((role) => ({ name: role.name })),
  };
}

export const authRepository = {
  async login(username: string, password: string): Promise<LoginResponse> {
    // v1 acepta usuario o email en un único campo `identifier`.
    const { data } = await apiClient.post('/auth/password/sign-in', {
      identifier: username,
      password,
    });
    const { session, user } = SessionResponseSchema.parse(data);
    return {
      token: session.accessToken,
      refreshToken: session.refreshToken,
      user: toAuthUser(user),
    };
  },

  async getMe(): Promise<AuthUser> {
    const { data } = await apiClient.get('/auth/me');
    return toAuthUser(CurrentUserDto.parse(data));
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/password/change', { currentPassword, newPassword });
  },

  async forgotPassword(email: string): Promise<void> {
    await apiClient.post('/auth/password/recovery/request', { email });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/password/recovery/complete', { credential: token, newPassword });
  },

  async requestOtp(email: string): Promise<void> {
    await apiClient.post('/auth/otp/request', { email });
  },

  async verifyOtp(email: string, code: string): Promise<OtpSession> {
    const { data } = await apiClient.post('/auth/otp/sign-in', { email, code });
    const { session, user } = SessionResponseSchema.parse(data);
    return {
      token: session.accessToken,
      refreshToken: session.refreshToken,
      user: toAuthUser(user),
    };
  },
};
